import React from 'react';
import type { Types } from '@fiverr-private/theme';
import { Container } from '@fiverr-private/layout_components';
import { TRANSITION_DURATION } from './RollupAnimation.constants';
import { RollupAnimationFront } from './RollupAnimationFront';
import { RollupAnimationBack } from './RollupAnimationBack';

export interface RollupAnimationProps {
  RollupBack: React.ReactNode;
  RollupFront: React.ReactNode;
  isOpen: boolean;
  onStateChange: () => void;
  handleHeight?: number;
  height: Types.ResponsiveHeightType;
  backgroundColor?: Types.SelectorBackgroundColorsType;
}

export const RollupAnimation: React.FC<RollupAnimationProps> = ({
  height,
  RollupBack,
  handleHeight = 230,
  RollupFront,
  isOpen,
  onStateChange,
  backgroundColor = 'grey_200',
}) => {
  const renderRollupHandle = () => (
    <Container
      onClick={onStateChange}
      borderTopWidth="sm"
      position="absolute"
      bottom={0}
      left={0}
      width="100%"
      height={`${handleHeight}px`}
      display="flex"
      alignItems="flexEnd"
      justifyContent="center"
      paddingBottom="6"
      cursor={isOpen ? 'default' : 'pointer'}
      pointerEvents={isOpen ? 'none' : undefined}
      zIndex="2"
      opacity={isOpen ? '0' : '1'}
      transition={`opacity ${TRANSITION_DURATION} ease-in-out`}
    >
      <Container width="60px" height="4px" borderRadius="lg" backgroundColor="grey_1000" />
    </Container>
  );

  return (
    <Container position="relative" width="100%" height={height} overflow="hidden" backgroundColor={backgroundColor}>
      {/* Back content - only visible when front slides up */}
      <Container
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        zIndex="1"
        opacity={isOpen ? '0' : '1'}
        pointerEvents={isOpen ? 'none' : undefined}
        transition={`opacity ${TRANSITION_DURATION} ease-in-out`}
      >
        <RollupAnimationBack handleHeight={handleHeight} backgroundColor={backgroundColor}>
          {RollupBack}
        </RollupAnimationBack>
      </Container>
      {/* Front content - slides up to reveal back */}
      <Container
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        zIndex="2"
        transform={isOpen ? 'translateY(0)' : `translateY(calc(-100% + ${handleHeight}px))`}
        transition={`transform ${TRANSITION_DURATION} ease-in-out, border-radius ${TRANSITION_DURATION} ease-in-out, box-shadow ${TRANSITION_DURATION} ease-in-out`}
        backgroundColor="white"
        borderRadius={isOpen ? 'none' : '3xl'}
        boxShadow={isOpen ? undefined : 'z3'}
      >
        {renderRollupHandle()}
        <RollupAnimationFront isOpen={isOpen}>{RollupFront}</RollupAnimationFront>
      </Container>
    </Container>
  );
};
